import { HttpMethod } from '@vision/ui/enums';
import { HttpExtraOptions } from '@vision/ui/interfaces';
import { TicketManagerApiRequest } from '../ticket-manager.api';

const TICKET_MANAGER_INNER_LOOP_ENDPOINT = '/v2/inner_loop';

interface TicketManagerTicketStatusRequestDto {
  category: 'open';
  name: string;
}

export const toTicketStatusRequestDto = ({ name }: { name: string }): TicketManagerTicketStatusRequestDto => ({
  category: 'open',
  name,
});

export const buildGetProjectTicketStatusesRequest = ({
  projectId,
  query = '',
}: {
  projectId: string;
  query?: string;
}): TicketManagerApiRequest => {
  const queryString = new URLSearchParams(query).toString();

  return {
    method: HttpMethod.GET,
    url: `${TICKET_MANAGER_INNER_LOOP_ENDPOINT}/${projectId}/ticket_statuses${queryString ? `?${queryString}` : ''}`,
  };
};

export const buildCreateTicketStatusRequest = ({
  name,
  projectId,
  extraOptions,
}: {
  name: string;
  projectId: string;
  extraOptions?: HttpExtraOptions;
}): TicketManagerApiRequest => ({
  data: toTicketStatusRequestDto({ name }),
  extraOptions,
  method: HttpMethod.POST,
  url: `${TICKET_MANAGER_INNER_LOOP_ENDPOINT}/${projectId}/ticket_statuses`,
});

export const buildUpdateTicketStatusRequest = ({
  name,
  projectId,
  statusId,
}: {
  name: string;
  projectId: string;
  statusId: string;
}): TicketManagerApiRequest => ({
  data: toTicketStatusRequestDto({ name }),
  method: HttpMethod.POST,
  url: `${TICKET_MANAGER_INNER_LOOP_ENDPOINT}/${projectId}/ticket_statuses/${statusId}/update`,
});

export const buildDeleteTicketStatusRequest = ({
  projectId,
  statusId,
  extraOptions,
}: {
  projectId: string;
  statusId: string;
  extraOptions?: HttpExtraOptions;
}): TicketManagerApiRequest => ({
  extraOptions,
  method: HttpMethod.POST,
  url: `${TICKET_MANAGER_INNER_LOOP_ENDPOINT}/${projectId}/ticket_statuses/${statusId}/delete`,
});

export const buildGetStatusTransitionsRequest = ({
  projectId,
  query = '',
}: {
  projectId: string;
  query?: string;
}): TicketManagerApiRequest => {
  const queryString = new URLSearchParams(query).toString();

  return {
    method: HttpMethod.GET,
    url: `${TICKET_MANAGER_INNER_LOOP_ENDPOINT}/${projectId}/ticket_status_transitions${
      queryString ? `?${queryString}` : ''
    }`,
  };
};

export const buildCreateStatusTransitionRequest = ({
  fromStatusId,
  projectId,
  toStatusId,
}: {
  fromStatusId: string;
  projectId: string;
  toStatusId: string;
}): TicketManagerApiRequest => ({
  data: {
    from_status_id: fromStatusId,
    to_status_id: toStatusId,
  },
  extraOptions: {
    notification: {
      autoHandleError: false,
    },
  },
  method: HttpMethod.POST,
  url: `${TICKET_MANAGER_INNER_LOOP_ENDPOINT}/${projectId}/ticket_status_transitions`,
});

export const buildUpdateStatusTransitionRequest = ({
  fromStatusId,
  projectId,
  toStatusId,
  transitionId,
}: {
  fromStatusId: string;
  projectId: string;
  toStatusId: string;
  transitionId: string;
}): TicketManagerApiRequest => ({
  data: {
    from_status_id: fromStatusId,
    to_status_id: toStatusId,
  },
  method: HttpMethod.POST,
  url: `${TICKET_MANAGER_INNER_LOOP_ENDPOINT}/${projectId}/ticket_status_transitions/${transitionId}/update`,
});

export const buildDeleteStatusTransitionRequest = ({
  projectId,
  transitionId,
}: {
  projectId: string;
  transitionId: string;
}): TicketManagerApiRequest => ({
  method: HttpMethod.POST,
  url: `${TICKET_MANAGER_INNER_LOOP_ENDPOINT}/${projectId}/ticket_status_transitions/${transitionId}/delete`,
});

export const buildResetStatusTransitionsRequest = ({ projectId }: { projectId: string }): TicketManagerApiRequest => ({
  extraOptions: {
    notification: {
      autoHandleSuccess: false,
    },
  },
  method: HttpMethod.POST,
  url: `${TICKET_MANAGER_INNER_LOOP_ENDPOINT}/${projectId}/ticket_status_transitions/reset`,
});

export const buildGetTicketAvailableStatusesRequest = ({
  projectId,
  ticketId,
}: {
  projectId: string;
  ticketId: string;
}): TicketManagerApiRequest => ({
  method: HttpMethod.GET,
  url: `${TICKET_MANAGER_INNER_LOOP_ENDPOINT}/${projectId}/tickets/${ticketId}/statuses`,
});
